/**
 * Modal UI bookkeeping for the loop (M3 plan §2.5, D-TBD 11): maps and panels open over the running sim. While
 * `ui.modal` > 0 the loop zeroes flight input and skips the flight edge actions, and pointer-lock loss does not
 * pause; Esc goes to the top handler of `ui.escape` before it may pause. Pure apart from the pointer-lock calls.
 */
import { logger } from '../core/logger'
import type { PlayerControl } from '../input/playerInput'
import type { ControlFilter, UiState } from './feature'

/** The element that takes pointer lock (the game canvas). */
export type PointerLockTarget = Pick<HTMLElement, 'requestPointerLock'>

function lockedElement(): Element | null {
  return typeof document === 'undefined' ? null : document.pointerLockElement
}

/**
 * `relock()` asks for pointer lock again (call it inside the gesture that closed the last modal); it does nothing
 * when `requireLock` is false (test builds) or the canvas already holds the lock. A refusal is logged; the loop
 * pauses on the resulting `pointerlockerror` because no modal is open any more.
 */
export function createUiState(canvas: PointerLockTarget, requireLock: boolean): UiState {
  return {
    modal: 0,
    escape: [],
    relock(): void {
      if (!requireLock || lockedElement() === canvas) return
      try {
        Promise.resolve(canvas.requestPointerLock()).catch((err: unknown) => logger.warn('Pointer lock refused after a modal', err))
      } catch (err: unknown) {
        logger.warn('Pointer lock refused after a modal', err)
      }
    },
  }
}

/** Offers Esc to the handlers from the top of the stack down; true when one consumed it. */
export function consumeEscape(ui: UiState): boolean {
  for (let i = ui.escape.length - 1; i >= 0; i--) if (ui.escape[i]()) return true
  return false
}

/**
 * Opens one modal layer: counts it, pushes its Esc handler and releases pointer lock. Returns the closer, which
 * undoes both and, when this was the last modal and `relock` is not false, calls `ui.relock()` (so call it from the
 * closing gesture). The closer is idempotent.
 */
export function enterModal(ui: UiState, onEscape: () => boolean): (relock?: boolean) => void {
  ui.modal++
  ui.escape.push(onEscape)
  if (lockedElement() !== null) document.exitPointerLock()
  let open = true
  return (relock = true): void => {
    if (!open) return
    open = false
    ui.modal = ui.modal > 0 ? ui.modal - 1 : 0
    const i = ui.escape.lastIndexOf(onEscape)
    if (i >= 0) ui.escape.splice(i, 1)
    if (relock && ui.modal === 0) ui.relock()
  }
}

/**
 * Per tick, after `readPlayerControl`: with a modal open every axis, boost and fire is zeroed; then the filters run
 * in registration order (cruise turn limits, M2's auto-dock pilot). Allocation-free (D-014).
 */
export function applyControlFilters(filters: readonly ControlFilter[], c: PlayerControl, modal: boolean): void {
  if (modal) {
    c.surge = 0
    c.sway = 0
    c.heave = 0
    c.pitch = 0
    c.yaw = 0
    c.roll = 0
    c.boost = false
    c.firePrimary = false
    c.fireSecondary = false
  }
  for (let i = 0; i < filters.length; i++) filters[i](c)
}
